import './timeslotPicker.scss';

import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ChipGroup,
  Pill,
  PriceColor,
  Skeleton,
  StockColor,
  TimeslotChip,
} from '@eliberty/design-system-react';

import { ErrorMessage, Tabs } from '~atoms';
import { formatPrice, getHourFromTimeslot, getLocale } from '~helpers';
import { Timeslot } from '~types/appsync.types';
import { extractTime } from '~utils';

interface TimeslotPickerProps {
  /** The time slots to display in selector */
  timeslots: Timeslot[];
  /** The selected time slot */
  selected?: Timeslot;
  /** The callback function called when a slot is selected */
  onTimeslotSelect: (timeslot: Timeslot) => void;
  /** Whether the component is in loading state */
  isLoading?: boolean;
  /** Number of participants to fit in a timeslot */
  participantsCount?: number;
  /** Currency used to format prices */
  currency?: string;
  /** Mapping of timeslot externalId to price in cents */
  timeslotPrices?: Record<string, number>;
}

const TimeslotPicker = ({
  timeslots,
  selected,
  onTimeslotSelect,
  isLoading = false,
  participantsCount = 0,
  currency = 'EUR',
  timeslotPrices,
}: TimeslotPickerProps) => {
  const intl = useIntl();
  const locale = getLocale();

  const handleSelectTimeslot = (timeslotId: string) => {
    const ts = timeslots.find((t) => `${t.externalId}` === timeslotId);
    onTimeslotSelect(ts!);
  };

  const firstTimeslot = timeslots[0];
  const isDaily = firstTimeslot?.dailyBooking ?? false;
  const dailyCount =
    firstTimeslot && !firstTimeslot.quotaUnlimited ? firstTimeslot.quotaAvailable : null;
  const filteredTimeslots = timeslots.filter(
    (t) => t.quotaUnlimited || t.quotaAvailable >= participantsCount,
  );

  const morningSlots = useMemo(
    () => filteredTimeslots.filter((t) => getHourFromTimeslot(t) < 12),
    [filteredTimeslots],
  );
  const afternoonSlots = useMemo(
    () => filteredTimeslots.filter((t) => getHourFromTimeslot(t) >= 12),
    [filteredTimeslots],
  );

  const shouldSplit =
    filteredTimeslots.length >= 15 && morningSlots.length > 0 && afternoonSlots.length > 0;

  const bestPriceTimeslotIds = useMemo(() => {
    if (!timeslotPrices) {
      return undefined;
    }

    const collectBestPriceIds = (slots: Timeslot[]) => {
      if (slots.length === 0) {
        return undefined;
      }

      let bestPrice = Infinity;
      const ids = new Set<string>();

      slots.forEach((timeslot) => {
        const price = timeslotPrices[timeslot.externalId];
        if (typeof price !== 'number' || price <= 0) {
          return;
        }
        if (price < bestPrice) {
          ids.clear();
          ids.add(timeslot.externalId);
          bestPrice = price;
          return;
        }
        if (price === bestPrice) {
          ids.add(timeslot.externalId);
        }
      });

      return ids.size > 0 ? ids : undefined;
    };

    if (shouldSplit) {
      const segments = [
        collectBestPriceIds(morningSlots),
        collectBestPriceIds(afternoonSlots),
      ].filter(Boolean) as Array<Set<string>>;
      if (segments.length === 0) {
        return undefined;
      }
      return segments.reduce((acc, segment) => {
        segment.forEach((id) => acc.add(id));
        return acc;
      }, new Set<string>());
    }

    return collectBestPriceIds(filteredTimeslots);
  }, [afternoonSlots, filteredTimeslots, morningSlots, shouldSplit, timeslotPrices]);

  const renderDailyContent = () => {
    if (!firstTimeslot || firstTimeslot.quotaUnlimited) return null;
    if (dailyCount && dailyCount > 0) {
      return (
        <span data-testid='DailyBookingText'>
          <Pill
            text={intl.formatMessage(
              {
                defaultMessage: '{count, plural, one {# seat} other {# seats}}',
                id: 'seats',
              },
              { count: dailyCount },
            )}
          />
        </span>
      );
    }
    return (
      <span className='TimeslotPicker_hour' data-testid='DailyBookingText'>
        <ErrorMessage
          defaultMessage='Plus de créneaux disponibles à la réservation.'
          messageId='noSeats'
        />
      </span>
    );
  };

  const getPriceLabel = (timeslot: Timeslot) => {
    if (!timeslotPrices) {
      return undefined;
    }
    const rawPrice = timeslotPrices[timeslot.externalId];
    if (rawPrice === undefined || rawPrice <= 0) {
      return undefined;
    }
    return formatPrice(locale, currency, rawPrice / 100);
  };

  return (
    <div className='TimeslotPicker'>
      <div className='TimeslotPicker_title'>
        {isDaily ? (
          <FormattedMessage defaultMessage='Availability' id='availability' />
        ) : (
          <FormattedMessage defaultMessage='Choose a timeslot' id='chooseATimeslot' />
        )}
      </div>
      {(() => {
        if (isLoading) {
          return (
            <div className='TimeslotPicker_slots'>
              <Skeleton data-testid='Skeleton' width='56px' height='56px' />
              <Skeleton data-testid='Skeleton' width='56px' height='56px' />
              <Skeleton data-testid='Skeleton' width='56px' height='56px' />
              <Skeleton data-testid='Skeleton' width='56px' height='56px' />
            </div>
          );
        }

        if (isDaily && firstTimeslot) {
          return renderDailyContent();
        }

        if (filteredTimeslots.length === 0) {
          return (
            <span className='TimeslotPicker_hour' data-testid='NoSeatsText'>
              <ErrorMessage
                defaultMessage='Plus de créneaux disponibles à la réservation.'
                messageId='noSeats'
              />
            </span>
          );
        }

        const renderSlots = (slots: Timeslot[]) => (
          <div className='TimeslotPicker_slots'>
            <ChipGroup value={selected?.externalId} onChange={handleSelectTimeslot}>
              {/* Attention : doit-on ajouter un delta de temps pour les réservations quotidiennes ??*/}
              {/* cf touch : const delay = getConfigByKey(apiConfig, 'booking_delay_select_unit', 10);*/}
              {slots.map((timeslot: Timeslot) => {
                const priceLabel = getPriceLabel(timeslot);
                const isBestPrice = !!bestPriceTimeslotIds?.has(timeslot.externalId);
                let priceColor: PriceColor | undefined;
                if (priceLabel) {
                  priceColor = isBestPrice ? PriceColor.Green : PriceColor.Blue;
                }
                return (
                  <TimeslotChip
                    key={timeslot.externalId}
                    disabled={timeslot.quotaAvailable === 0}
                    data-best-price={isBestPrice ? 'true' : undefined}
                    label={extractTime(timeslot.date)}
                    stock={intl.formatMessage(
                      {
                        defaultMessage: '{count, plural, one {# seat} other {# seats}}',
                        id: 'seats',
                      },
                      { count: timeslot.quotaAvailable },
                    )}
                    stockColor={timeslot.quotaAvailable > 5 ? StockColor.Green : StockColor.Red}
                    checked={selected?.externalId === timeslot.externalId}
                    value={timeslot.externalId}
                    price={priceLabel}
                    priceColor={priceColor}
                  />
                );
              })}
            </ChipGroup>
          </div>
        );

        if (shouldSplit) {
          return (
            <Tabs
              defaultValue={
                selected && getHourFromTimeslot(selected) >= 12 ? 'afternoon' : 'morning'
              }
              tabs={[
                {
                  content: renderSlots(morningSlots),
                  label: intl.formatMessage({ defaultMessage: 'Matin', id: 'morning' }),
                  value: 'morning',
                },
                {
                  content: renderSlots(afternoonSlots),
                  label: intl.formatMessage({ defaultMessage: 'Après-midi', id: 'afternoon' }),
                  value: 'afternoon',
                },
              ]}
            />
          );
        }

        return renderSlots(filteredTimeslots);
      })()}
    </div>
  );
};

export default TimeslotPicker;
